import type { TranslationKey } from "src/types/translationKey";
import type { CareTeamCardBadge } from "src/ui/components/CareTeamSection/CareTeamRoleCard";
import type { CareTeamCardVariant } from "src/ui/components/CareTeamSection/CardBackground";

/**
 * Rich detail content for a Care Team role, shown in the detail bottom
 * sheet when a card is tapped.
 *
 * Fields default to the shared eyebrows ("Their expertise", "How to
 * benefit") — override `expertiseTitleKey` per role when the copy needs
 * to differ (Sophie uses "What they do").
 */
export interface CareTeamRoleDetail {
  subtitleKey: TranslationKey;
  expertiseTitleKey?: TranslationKey;
  expertiseBodyKey: TranslationKey;
  whereTheyHelpKeys: TranslationKey[];
  howToBenefitBodyKey: TranslationKey;
  /**
   * Optional pill rendered next to the avatar stack in the detail-sheet
   * header. Sophie uses this to communicate "Always on | 24/7" without
   * reusing the small count pill inside `AvatarStack`.
   */
  headerPillKey?: TranslationKey;
  /**
   * Optional callout rendered between "How to benefit" and the footer.
   * Used by Sophie to note that she doesn't give medical advice.
   */
  noteKey?: TranslationKey;
  /**
   * Disclaimer shown under the "Ask Sophie" CTA. Providers get a
   * different message ("Visits are arranged by your Care Team") because
   * you don't chat directly with a doctor.
   */
  disclaimerKey?: TranslationKey;
}

/**
 * A single Care Team role shown as a card in the home-screen carousel
 * and rendered in the detail bottom sheet on tap.
 *
 * v1 is static/hardcoded — real Sanity/API wiring is a follow-up ticket.
 * The shape here mirrors what the real API is expected to return so the
 * wiring change is a data-source swap only.
 */
export interface CareTeamRole {
  id: string;
  titleKey: TranslationKey;
  /**
   * Optional card-only override for `titleKey`. Used when the full role
   * title (rendered in the detail sheet) is too long or reads oddly on
   * the small card — e.g. Sophie's card shows "Virtual Assistant" while
   * the detail sheet keeps "Sophie, Virtual Assistant".
   */
  cardTitleKey?: TranslationKey;
  variant: CareTeamCardVariant;
  badge: CareTeamCardBadge;
  detail: CareTeamRoleDetail;
}

/**
 * Sanity CDN URLs for each role's featured members. Ordered — only the
 * first three render in the stack today (see `AvatarStack.slice(0, 3)`);
 * extras stay in data so the visible count can grow without a CMS
 * re-key.
 */
const HEALTH_COACH_AVATARS = [
  "https://cdn.sanity.io/images/g38rxyoc/production/c162544f790eb27378b321c5c4d852f98749205e-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/eee690c0292d67f2b61f3a129fa255092e83c41e-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/6cba9ab150058506697a687c6b3283232e08a5a5-200x200.jpg"
];

const REGISTERED_DIETITIAN_AVATARS = [
  "https://cdn.sanity.io/images/g38rxyoc/production/e332ed94f3910d89649f2349cafad6fcd446ee22-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/78adcd8b5b116c8b0760eff58f24f4069a61f904-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/97ba6d00ac24269d8813d272236184a55186453c-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/f5689e45dc22af4ca38ebab7037b3073b5af44f3-200x200.jpg"
];

const REGISTERED_NURSE_AVATARS = [
  "https://cdn.sanity.io/images/g38rxyoc/production/15bb163d0b44b7413f00de3f8441e642fb489d19-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/d1e4d70a2db1627acaf14bf4390f3e3d97045fa6-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/035c8e99ee2c33207d254f00987e1490adbde96a-200x200.jpg"
];

const PHARMACIST_AVATARS = [
  "https://cdn.sanity.io/images/g38rxyoc/production/4b6759d3554cee12e0ebe06c07b26b95404a2777-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/595ae47204e6c0071f0c040d6d16c082af53ecf0-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/a520f0203bdc4a5345019d5b743dc133d8758255-200x200.jpg"
];

const DOCTORS_AND_SPECIALISTS_AVATARS = [
  "https://cdn.sanity.io/images/g38rxyoc/production/9921a8acf0631b13bc373ace43949b0edf29235b-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/e56da054848f6a4f62f4910026f2ea9b1962139d-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/cc7fa6575664bd51865c3379894f1a8746ee493d-200x200.jpg"
];

const PATIENT_CARE_ADVOCATE_AVATARS = [
  "https://cdn.sanity.io/images/g38rxyoc/production/d54f01cdf204cd1152515599ab57f318d8bfb59f-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/af9905a0273af38e47806e374d064153c2a2f355-200x200.jpg",
  "https://cdn.sanity.io/images/g38rxyoc/production/1d460017b3787390e2359a44dc374ffb7b3bd78f-200x200.jpg"
];

const SOPHIE_AVATARS = [
  "https://cdn.sanity.io/images/g38rxyoc/production/4561a70101c221282f4290356d290a1cace95ae1-40x40.png"
];

/**
 * Provider-role badge: featured member avatars stacked + a trailing "+"
 * pill hinting that more team members exist. The pill sits behind the
 * avatars in the paint order so the "+" reads as a "more" affordance
 * rather than a prominent count.
 */
const stackBadge = (avatars: string[]): CareTeamCardBadge => ({
  avatars,
  label: "+"
});

/**
 * Ordered Care Team roles rendered on the home screen. Order is
 * product-defined and must not be changed without a design review.
 */
export const CARE_TEAM_ROLES: CareTeamRole[] = [
  {
    id: "healthCoaches",
    titleKey: "careTeam.role.healthCoaches",
    cardTitleKey: "careTeam.role.healthCoaches.card",
    variant: "healthCoaches",
    badge: stackBadge(HEALTH_COACH_AVATARS),
    detail: {
      subtitleKey: "careTeam.detail.healthCoaches.subtitle",
      expertiseBodyKey: "careTeam.detail.healthCoaches.expertiseBody",
      whereTheyHelpKeys: [
        "careTeam.detail.healthCoaches.whereTheyHelp.1",
        "careTeam.detail.healthCoaches.whereTheyHelp.2",
        "careTeam.detail.healthCoaches.whereTheyHelp.3"
      ],
      howToBenefitBodyKey: "careTeam.detail.healthCoaches.howToBenefit",
      disclaimerKey: "careTeam.detail.disclaimer.default"
    }
  },
  {
    id: "registeredDietitians",
    titleKey: "careTeam.role.registeredDietitians",
    cardTitleKey: "careTeam.role.registeredDietitians.card",
    variant: "registeredDietitians",
    badge: stackBadge(REGISTERED_DIETITIAN_AVATARS),
    detail: {
      subtitleKey: "careTeam.detail.registeredDietitians.subtitle",
      expertiseBodyKey: "careTeam.detail.registeredDietitians.expertiseBody",
      whereTheyHelpKeys: [
        "careTeam.detail.registeredDietitians.whereTheyHelp.1",
        "careTeam.detail.registeredDietitians.whereTheyHelp.2",
        "careTeam.detail.registeredDietitians.whereTheyHelp.3",
        "careTeam.detail.registeredDietitians.whereTheyHelp.4"
      ],
      howToBenefitBodyKey: "careTeam.detail.registeredDietitians.howToBenefit",
      disclaimerKey: "careTeam.detail.disclaimer.default"
    }
  },
  {
    id: "registeredNurses",
    titleKey: "careTeam.role.registeredNurses",
    cardTitleKey: "careTeam.role.registeredNurses.card",
    variant: "registeredNurses",
    badge: stackBadge(REGISTERED_NURSE_AVATARS),
    detail: {
      subtitleKey: "careTeam.detail.registeredNurses.subtitle",
      expertiseBodyKey: "careTeam.detail.registeredNurses.expertiseBody",
      whereTheyHelpKeys: [
        "careTeam.detail.registeredNurses.whereTheyHelp.1",
        "careTeam.detail.registeredNurses.whereTheyHelp.2",
        "careTeam.detail.registeredNurses.whereTheyHelp.3"
      ],
      howToBenefitBodyKey: "careTeam.detail.registeredNurses.howToBenefit",
      disclaimerKey: "careTeam.detail.disclaimer.default"
    }
  },
  {
    id: "pharmacist",
    titleKey: "careTeam.role.pharmacist",
    variant: "pharmacist",
    badge: stackBadge(PHARMACIST_AVATARS),
    detail: {
      subtitleKey: "careTeam.detail.pharmacist.subtitle",
      expertiseBodyKey: "careTeam.detail.pharmacist.expertiseBody",
      whereTheyHelpKeys: [
        "careTeam.detail.pharmacist.whereTheyHelp.1",
        "careTeam.detail.pharmacist.whereTheyHelp.2"
      ],
      howToBenefitBodyKey: "careTeam.detail.pharmacist.howToBenefit",
      disclaimerKey: "careTeam.detail.disclaimer.default"
    }
  },
  {
    id: "doctorsAndSpecialists",
    titleKey: "careTeam.role.doctorsAndSpecialists",
    cardTitleKey: "careTeam.role.doctorsAndSpecialists.card",
    variant: "doctorsAndSpecialists",
    badge: stackBadge(DOCTORS_AND_SPECIALISTS_AVATARS),
    detail: {
      subtitleKey: "careTeam.detail.doctorsAndSpecialists.subtitle",
      expertiseBodyKey: "careTeam.detail.doctorsAndSpecialists.expertiseBody",
      whereTheyHelpKeys: [
        "careTeam.detail.doctorsAndSpecialists.whereTheyHelp.1",
        "careTeam.detail.doctorsAndSpecialists.whereTheyHelp.2",
        "careTeam.detail.doctorsAndSpecialists.whereTheyHelp.3"
      ],
      howToBenefitBodyKey: "careTeam.detail.doctorsAndSpecialists.howToBenefit",
      // Providers can't be chatted with directly — visits are arranged by
      // the Care Team, so this disclaimer overrides the default.
      disclaimerKey: "careTeam.detail.disclaimer.providers"
    }
  },
  {
    id: "patientCareAdvocates",
    titleKey: "careTeam.role.patientCareAdvocates",
    cardTitleKey: "careTeam.role.patientCareAdvocates.card",
    variant: "patientCareAdvocates",
    badge: stackBadge(PATIENT_CARE_ADVOCATE_AVATARS),
    detail: {
      subtitleKey: "careTeam.detail.patientCareAdvocates.subtitle",
      expertiseBodyKey: "careTeam.detail.patientCareAdvocates.expertiseBody",
      whereTheyHelpKeys: [
        "careTeam.detail.patientCareAdvocates.whereTheyHelp.1",
        "careTeam.detail.patientCareAdvocates.whereTheyHelp.2",
        "careTeam.detail.patientCareAdvocates.whereTheyHelp.3",
        "careTeam.detail.patientCareAdvocates.whereTheyHelp.4",
        "careTeam.detail.patientCareAdvocates.whereTheyHelp.5",
        "careTeam.detail.patientCareAdvocates.whereTheyHelp.6"
      ],
      howToBenefitBodyKey: "careTeam.detail.patientCareAdvocates.howToBenefit",
      disclaimerKey: "careTeam.detail.disclaimer.default"
    }
  },
  {
    id: "sophie",
    titleKey: "careTeam.role.sophie",
    cardTitleKey: "careTeam.role.sophie.card",
    variant: "sophie",
    badge: {
      avatars: SOPHIE_AVATARS,
      label: "24/7"
    },
    detail: {
      subtitleKey: "careTeam.detail.sophie.subtitle",
      // Sophie's section eyebrow reads "What they do" instead of the
      // shared "Their expertise" because she isn't a clinician.
      expertiseTitleKey: "careTeam.detail.sophie.expertiseTitle",
      expertiseBodyKey: "careTeam.detail.sophie.expertiseBody",
      whereTheyHelpKeys: [
        "careTeam.detail.sophie.whereTheyHelp.1",
        "careTeam.detail.sophie.whereTheyHelp.2",
        "careTeam.detail.sophie.whereTheyHelp.3",
        "careTeam.detail.sophie.whereTheyHelp.4"
      ],
      headerPillKey: "careTeam.detail.sophie.alwaysOn",
      noteKey: "careTeam.detail.sophie.note",
      howToBenefitBodyKey: "careTeam.detail.sophie.howToBenefit"
    }
  }
];

/**
 * Look up a Care Team role by its `id`. Returns `undefined` if no role
 * matches — callers should guard against that (e.g. when driving the
 * detail sheet from Cubit state).
 */
export const findCareTeamRole = (id: string | null): CareTeamRole | undefined =>
  CARE_TEAM_ROLES.find((role) => role.id === id);
